import { Reservation, StatusForAgentEnum } from '@appTypes/api.types';
import { ConversationAttributesModel, ConversationReferenceTypeEnum } from '@appTypes/chat-api.types';
import { IValueLabel } from '@appTypes/common.types';
import { differenceInYears } from 'date-fns';

export const NGXS_RESERVATIONS_SEARCH_FORM = 'reservations.searchForm';
export const NGXS_RESERVATION_TOURIST_DOCUMENTS_UPLOAD_FORM = 'reservations.touristDocumentsUploadForm.form';

export const RESERVATIONS_CHAT_REFERENCE = ConversationReferenceTypeEnum.RESERVATION;
export const RESERVATION_STATUS_FILTERS: IValueLabel<StatusForAgentEnum>[] = [
  { value: StatusForAgentEnum.New, label: 'New', styleClass: 'text-blue-500' },
  { value: StatusForAgentEnum.OnProcess, label: 'On Process', styleClass: 'text-kz-yellow-700' },
  { value: StatusForAgentEnum.Confirmed, label: 'Confirmed', styleClass: 'text-kz-success' },
  { value: StatusForAgentEnum.Modifying, label: 'Modifying', styleClass: 'text-teal-500' },
  { value: StatusForAgentEnum.Canceled, label: 'Rejected', styleClass: 'text-kz-danger' },
  // ADLC-4 (agency payment rules): the agency filters its reservations with a step past its due date. The status is set by
  // reservations-api from the payment plans index; the screens mark it with "! " too, so red is never the only signal.
  { value: StatusForAgentEnum.Overdued, label: 'Overdued', styleClass: 'text-kz-danger' }
];

export const RES_TOURIST_DOCUMENTS_UPLOAD_FILE_TYPES = 'image/*, .pdf, .docx, .xlsx';
export const RES_MAXIMUM_ATTACHMENTS_SIZE_MB = 5;

/**
 * Longest title the tourist document upload form accepts (KM-5638). The title becomes the Event
 * title in NugiosCore → Reservation → Workdesc, where an overlong one hurts readability.
 */
export const RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH = 50;
/** Shown under the title once it is over {@link RES_TOURIST_DOCUMENT_TITLE_MAX_LENGTH} — the ticket's own wording. */
export const RES_TOURIST_DOCUMENT_TITLE_TOO_LONG_MESSAGE = 'Maximum {maxLength} characters allowed.';
/**
 * Posted to the reservation chat once a tourist document is uploaded. It names the tourist the
 * document belongs to, so a reservation with several tourists still tells them apart (KM-5638).
 */
export const RES_TOURIST_DOCUMENT_UPLOADED_CHAT_MESSAGE =
  'The file has been uploaded for the tourist ({touristName}) from the Reservation list.';

/**
 * `errorCode` the API answers with when a tourist document upload is refused because the
 * reservation is no longer `New` in NugiosCore (KM-5604 / KM-5646).
 */
export const RES_TOURIST_DOCUMENT_UPLOAD_NOT_ALLOWED_ERROR_CODE = 'UNABLE_UPLOAD_DOCUMENT_DUE_RES_STATUS';
/**
 * Shown in the upload popup of a reservation that is not `New`, and in place of a refusal the API
 * sends no text of its own for (KM-5604). The API's own `userErrorDescription` is this same text.
 */
export const RES_TOURIST_DOCUMENT_UPLOAD_ALLOWED_STATUS_MESSAGE =
  'File upload is allowed only for reservations with "New" status.';
/**
 * Shown in place of the API's refusal once the refused reservation was re-read and is indeed no
 * longer `New` — `{status}` is the localized label of the status it moved to (KM-5604).
 */
export const RES_TOURIST_DOCUMENT_UPLOAD_STATUS_CHANGED_MESSAGE =
  'File upload is not allowed: the reservation status has changed to “{status}”.';
/** Shown for any other upload failure the API gives no text for. */
export const RES_TOURIST_DOCUMENT_UPLOAD_FAILED_MESSAGE =
  'Something went wrong while uploading document, please try again later';

/**
 * `errorCode` the API answers with instead of a file when the requested document (voucher, air
 * ticket, insurance) was closed in NugiosCore after the Documents popup was opened — the
 * reservation is no longer confirmed, or it is confirmed with a debt and a manager has not opened
 * the documents by hand (KM-5777).
 */
export const RESERVATION_DOCUMENT_UNAVAILABLE_ERROR_CODE = 'DOCUMENT_UNAVAILABLE_FOR_DOWNLOAD';
/** Shown for that code when the API sends no usable text of its own. */
export const RESERVATION_DOCUMENT_UNAVAILABLE_MESSAGE =
  'The document is currently unavailable for download. Please contact your manager.';
/** Shown for any other download failure the API gives no text for. */
export const RESERVATION_DOCUMENT_DOWNLOAD_FAILED_MESSAGE = "Can't download document";

export const buildReservationConversationAttributes = (reservation: Reservation): ConversationAttributesModel => {
  const adultTourist = reservation.tourists?.find(
    (t) => t.birthDate && differenceInYears(new Date(), t.birthDate) >= 18
  );

  return {
    hotelName: reservation.hotelNames?.join(', '),
    contragentName: reservation.contragentName!,
    bookingStartDate: reservation.arrivalTime,
    bookingEndDate: reservation.departureTime,
    rfNumber: reservation.number!,
    countryName: reservation.arrivalCountryName!,
    touristLastName: adultTourist?.lastName ?? '',
    reservationAgentStatus: reservation.statusForAgent
  };
};
